import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import axios from 'src/utils/axios';
import axiosHeadOffice from 'src/utils/axiosHeadOffice';
import Page from 'src/components/Page';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    }
  },
  header: {
    marginBottom: theme.spacing(2)
  },
  title: {
    color: '#263238',
    fontWeight: 700
  },
  subtitle: {
    color: '#78909c',
    marginTop: theme.spacing(0.5)
  },
  card: {
    borderRadius: 12,
    boxShadow: '0 2px 12px -4px rgba(0, 0, 0, .14)',
    marginBottom: theme.spacing(2)
  },
  searchRow: {
    alignItems: 'center'
  },
  searchButton: {
    height: 40,
    minWidth: 116,
    backgroundColor: '#1E5128',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#155120'
    }
  },
  tableCard: {
    overflow: 'hidden'
  },
  tableHead: {
    backgroundColor: '#f4f6f8'
  },
  headCell: {
    color: '#546e7a',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.4px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  },
  bodyRow: {
    '&:hover': {
      backgroundColor: '#f9fbff'
    }
  },
  empty: {
    color: '#78909c',
    padding: theme.spacing(5, 2),
    textAlign: 'center'
  }
}));

const getValue = (row, ...keys) => {
  const key = keys.find(candidate => row[candidate] !== undefined && row[candidate] !== null);
  return key ? row[key] : '';
};

const normalizeRows = data => {
  const rows = Array.isArray(data) ? data : data?.Items || data?.items || data?.Data || data?.data || [];
  return rows.map(row => ({
    region: getValue(row, 'Region', 'RegionName', 'regionName'),
    city: getValue(row, 'City', 'CityName', 'cityName'),
    area: getValue(row, 'Area', 'AreaName', 'District', 'DistrictName', 'districtName'),
    neighborhood: getValue(row, 'Neighborhood', 'NeighborhoodName', 'neighborhoodName'),
    street: getValue(row, 'Street', 'StreetName', 'streetName'),
    apartment: getValue(row, 'Apartment', 'ApartmentName', 'apartmentName'),
    doorNr: getValue(row, 'DoorNr', 'DoorNumber', 'doorNr', 'doorNumber'),
    address: getValue(row, 'Address', 'address'),
    createDate: getValue(row, 'CreateDate', 'createDate')
  }));
};

function AreaSearch() {
  const classes = useStyles();
  const { t } = useTranslation('overview');
  const [searchText, setSearchText] = useState('');
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    let mounted = true;
    setCitiesLoading(true);

    axios
      .get('/GetCityRequest/')
      .then(response => {
        if (!mounted) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setCities(data
          .map(item => typeof item === 'string' ? item : getValue(item, 'City', 'city'))
          .map(item => String(item).trim())
          .filter(Boolean)
          .sort((first, second) => first.localeCompare(second)));
      })
      .catch(() => {
        if (mounted) setCities([]);
      })
      .finally(() => {
        if (mounted) setCitiesLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleSearch = async event => {
    if (event) event.preventDefault();
    setLoading(true);
    try {
      const response = await axiosHeadOffice.get('/GetComplaintByArea', {
        params: {
          city: city.trim(),
          searchText: searchText.trim()
        }
      });
      setRows(normalizeRows(response.data));
    } catch (error) {
      setRows([]);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  };

  return (
    <Page title={t('ASTtitle')}>
      <Container maxWidth="xl" className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h5" className={classes.title}>
            {t('ASTtitle')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t('ASTsubtitle')}
          </Typography>
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <form onSubmit={handleSearch}>
              <Grid container spacing={2} className={classes.searchRow}>
                <Grid item xs={12} sm>
                  <TextField
                    fullWidth
                    label={t('ASTsearchLabel')}
                    value={searchText}
                    onChange={event => setSearchText(event.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm>
                  <Autocomplete
                    options={cities}
                    value={city || null}
                    size="small"
                    loading={citiesLoading}
                    onChange={(event, newValue) => setCity(newValue || '')}
                    renderInput={params => (
                      <TextField
                        {...params}
                        fullWidth
                        label={t('ASTcity')}
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {citiesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Button
                    type="submit"
                    variant="contained"
                    className={classes.searchButton}
                    disabled={loading || !city.trim() || searchText.trim().length < 3}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                  >
                    {t('ASTsearch')}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        <Card className={`${classes.card} ${classes.tableCard}`}>
          <TableContainer>
            <Table aria-label={t('ASTtableLabel')}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headCell}>{t('ASTregion')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTcity')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTarea')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTneighborhood')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTstreet')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTapartment')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTdoorNumber')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTaddress')}</TableCell>
                  <TableCell className={classes.headCell}>{t('ASTcreateDate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? rows.map((row, index) => (
                  <TableRow key={`${row.region}-${row.city}-${row.area}-${index}`} className={classes.bodyRow}>
                    <TableCell>{row.region}</TableCell>
                    <TableCell>{row.city}</TableCell>
                    <TableCell>{row.area}</TableCell>
                    <TableCell>{row.neighborhood}</TableCell>
                    <TableCell>{row.street}</TableCell>
                    <TableCell>{row.apartment}</TableCell>
                    <TableCell>{row.doorNr}</TableCell>
                    <TableCell>{row.address}</TableCell>
                    <TableCell>{row.createDate}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9} className={classes.empty}>
                      {searched ? t('ASTnoResults') : t('ASTsearchPrompt')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Page>
  );
}

export default AreaSearch;